/**
 * Centralized axios client for the portfolio + admin CMS.
 * - Reads token from localStorage and attaches Authorization header.
 * - Surfaces friendly error messages.
 */
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "portfolio_admin_token";

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
});

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ---------------- Auth ----------------
export const authApi = {
  async login(email, password) {
    const { data } = await api.post("/auth/login", { email, password });
    console.log("LOGIN RESPONSE", data);
    localStorage.setItem(TOKEN_KEY, data.accessToken);
    return data.user;
  },
  async me() {
    const { data } = await api.get("/auth/me");
    return data;
  },
  logout() {
    localStorage.removeItem(TOKEN_KEY);
  },
  hasToken() {
    return !!localStorage.getItem(TOKEN_KEY);
  },
};

// ---------------- Public (used by public site) ----------------
export const publicApi = {
  async portfolio() {
    const { data } = await api.get("/public/portfolio");
    return data;
  },
  async contact(payload) {
    const { data } = await api.post("/contact", payload);
    return data;
  },
};

// ---------------- Admin generic CRUD ----------------
function crud(resource) {
  return {
    list: async () => (await api.get(`/admin/${resource}`)).data,
    create: async (payload) => (await api.post(`/admin/${resource}`, payload)).data,
    update: async (id, payload) => (await api.put(`/admin/${resource}/${id}`, payload)).data,
    remove: async (id) => (await api.delete(`/admin/${resource}/${id}`)).data,
    duplicate: async (id) => (await api.post(`/admin/${resource}/${id}/duplicate`)).data,
    reorder: async (items) => (await api.post(`/admin/${resource}/reorder`, { items })).data,
  };
}

export const adminApi = {
  dashboard: async () => (await api.get("/admin/dashboard")).data,
  projects: crud("projects"),
  experiences: crud("experiences"),
  skills: crud("skills"),
  education: crud("education"),
  certifications: crud("certifications"),
  achievements: crud("achievements"),
  // singletons
  hero: {
    get: async () => (await api.get("/admin/hero")).data,
    put: async (payload) => (await api.put("/admin/hero", payload)).data,
  },
  contact: {
    get: async () => (await api.get("/admin/contact-settings")).data,
    put: async (payload) => (await api.put("/admin/contact-settings", payload)).data,
  },
  settings: {
    get: async () => (await api.get("/admin/settings")).data,
    put: async (payload) => (await api.put("/admin/settings", payload)).data,
  },
  messages: {
    list: async () => (await api.get("/admin/messages")).data,
    remove: async (id) => (await api.delete(`/admin/messages/${id}`)).data,
  },
  uploadImage: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload/image", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data; // { url, filename }
  },
  uploadResume: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload/resume", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
};

export function absoluteUrl(maybePath) {
  if (!maybePath) return "";
  if (/^https?:\/\//.test(maybePath)) return maybePath;
  if (maybePath.startsWith("/uploads/") || maybePath.startsWith("/api/uploads/")) {
    const path = maybePath.startsWith("/api") ? maybePath : `/api${maybePath}`;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  }
  return maybePath; // frontend public file like /avatar.jpg
}
